import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { useDispatch } from 'react-redux';

import ReportIcon from '../../../assets/profileRedesign/report.svg';
import AbusiveIcon from '../../../assets/profileRedesign/report-abusive.svg';
import ChevronIcon from '../../../assets/profileRedesign/report-chevron.svg';
import InappropriateIcon from '../../../assets/profileRedesign/report-inappropriate.svg';
import ViolationIcon from '../../../assets/profileRedesign/report-violation.svg';
import { reportUserRequest } from '../../../store/auth/authActions';
import { ReportType } from '../../../store/types';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	RADIUS_MD,
} from '../Achievements/achievementsTheme';
import { GradientFill } from '../common/GradientFill';
import { Pressable } from '../common/Touchable';

/**
 * Figma "Profile / Modal —Other profile / REPORT" (4897:21399), page "BOGI DRAFT": the "Button /
 * Report" in another player's profile actions, and the "Dropdown / Report reasons" (4908:9962)
 * it opens just above itself. Picking a reason files the report straight away; the auth saga
 * shows the "Report sent" toast.
 */
type Props = {
	playerId: string;
	scale: number;
};

type SvgIcon = React.FC<SvgProps>;

const DANGER = '#FF4D3D';
/** "Button / Report" (4897:22107): 136 wide with its trailing chevron. */
const BUTTON = {
	height: 44,
	paddingX: 16,
	gap: 8,
	iconWidth: 24.23,
	iconHeight: 21.5,
	chevron: 18,
};
const MENU = { width: 260, gap: 8, padding: 6, radius: 16, item: 48, itemRadius: 10, icon: 24 };
const MENU_FILL: Array<[string, string]> = [
	['0', '#330200'],
	['1', '#120000'],
];

/** The file's three reasons, one per report type of the API's taxonomy (`ReportType`). */
const REASONS: Array<{
	labelKey: string;
	label: string;
	Icon: SvgIcon;
	type: ReportType;
}> = [
	{
		labelKey: 'T_SITE_REPORT_ABUSIVE',
		label: 'Abusive behavior',
		Icon: AbusiveIcon,
		type: ReportType.ABUSIVE_BEHAVIOR,
	},
	{
		labelKey: 'T_SITE_REPORT_RULES_VIOLATION',
		label: 'Violation of game rules',
		Icon: ViolationIcon,
		type: ReportType.GAME_RULES_VIOLATION,
	},
	{
		labelKey: 'T_SITE_REPORT_INAPPROPRIATE',
		label: 'Inappropriate content',
		Icon: InappropriateIcon,
		type: ReportType.INAPPROPRIATE_CONTENT,
	},
];

export const ProfileReportMenu = ({ playerId, scale }: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const styles = useMemo(() => createStyles(scale), [scale]);
	const s = (value: number) => value * scale;

	const report = (reason: (typeof REASONS)[number]) => {
		setIsOpen(false);
		dispatch(
			reportUserRequest({
				reportedId: playerId,
				reportType: reason.type,
				gameContext: 'PROFILE',
			}),
		);
	};

	return (
		<View style={styles.root}>
			{isOpen ? (
				<View style={styles.menu}>
					{/* Its own full-size layer: an SVG's 100% resolves against the menu's padded box. */}
					<View pointerEvents='none' style={StyleSheet.absoluteFill}>
						<GradientFill stops={MENU_FILL} radius={s(MENU.radius)} />
					</View>
					{REASONS.map((reason, index) => (
						<React.Fragment key={reason.label}>
							{index > 0 ? <View style={styles.divider} /> : null}
							<Pressable
								accessibilityRole='button'
								accessibilityLabel={t('T_SITE_A11Y_REPORT_FOR', {
									defaultValue: 'Report for {{reason}}',
									reason: t(reason.labelKey, { defaultValue: reason.label }).toLowerCase(),
								})}
								onPress={() => report(reason)}
								style={({ pressed }) => [styles.item, pressed && styles.itemPressed]}
							>
								<View>
									<reason.Icon width={s(MENU.icon)} height={s(MENU.icon)} />
								</View>
								<Text numberOfLines={1} style={styles.itemLabel}>
									{t(reason.labelKey, { defaultValue: reason.label })}
								</Text>
							</Pressable>
						</React.Fragment>
					))}
				</View>
			) : null}

			<Pressable
				accessibilityRole='button'
				accessibilityLabel={t('T_ReportPlayer', { defaultValue: 'Report player' })}
				accessibilityState={{ expanded: isOpen }}
				onPress={() => setIsOpen(open => !open)}
				style={styles.button}
			>
				<View>
					<ReportIcon width={s(BUTTON.iconWidth)} height={s(BUTTON.iconHeight)} />
				</View>
				<Text numberOfLines={1} style={styles.label}>
					{t('T_SITE_REPORT_BUTTON', { defaultValue: 'Report' })}
				</Text>
				{/* At rest it points up, where the menu opens; once open it flips down to close it. */}
				<View style={isOpen ? undefined : styles.chevronUp}>
					<ChevronIcon width={s(BUTTON.chevron)} height={s(BUTTON.chevron)} />
				</View>
			</Pressable>
		</View>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;

	return StyleSheet.create({
		root: {
			height: '100%',
			// Above the add-friend button, so the menu it opens is not painted under it.
			zIndex: 2,
		},
		button: {
			height: '100%',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			columnGap: s(BUTTON.gap),
			paddingHorizontal: s(BUTTON.paddingX),
			borderRadius: s(RADIUS_MD),
			borderWidth: Math.max(1, s(1)),
			borderColor: DANGER,
			// surface/overlay
			backgroundColor: achievementsColors.overlay,
			boxShadow: `0px 0px ${s(5)}px 0px rgba(255, 77, 61, 0.45)`,
		},
		// Sofia Sans Bold 14, line-height 1, letter-spacing 1%.
		label: {
			color: '#F69D95',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(14),
			fontWeight: '700',
			lineHeight: s(14),
			letterSpacing: s(0.14),
		},
		chevronUp: {
			transform: [{ rotate: '180deg' }],
		},
		menu: {
			position: 'absolute',
			left: 0,
			bottom: s(BUTTON.height + MENU.gap),
			width: s(MENU.width),
			padding: s(MENU.padding),
			borderRadius: s(MENU.radius),
			borderWidth: Math.max(1, s(1)),
			borderColor: DANGER,
			boxShadow: `0px 0px ${s(10)}px 0px rgba(255, 77, 61, 0.45), 0px ${s(10)}px ${s(24)}px 0px rgba(0, 0, 0, 0.6)`,
		},
		item: {
			height: s(MENU.item),
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: s(12),
			paddingHorizontal: s(10),
			borderRadius: s(MENU.itemRadius),
		},
		itemPressed: {
			backgroundColor: 'rgba(255, 77, 61, 0.12)',
		},
		divider: {
			height: 1,
			backgroundColor: 'rgba(255, 77, 61, 0.22)',
		},
		// Sofia Sans SemiBold 14, line-height 1.2, gold/50.
		itemLabel: {
			flex: 1,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(14),
			fontWeight: '600',
			lineHeight: s(14 * 1.2),
		},
	});
};

export default ProfileReportMenu;
